// features/tutorials/components/TutorialHero.tsx
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Command, Clock, BookOpen, GraduationCap, ArrowRight, Sparkles } from 'lucide-react';

interface TutorialHeroProps {
    onSearchFocus: () => void;
    overallProgress: number;
    resumeLearning: { module: any; lesson: any } | null;
    onResumeLearning: (lesson: any) => void;
}

export const TutorialHero: React.FC<TutorialHeroProps> = ({
    onSearchFocus,
    overallProgress,
    resumeLearning,
    onResumeLearning,
}) => {
    const [titleText, setTitleText] = useState('');
    const fullTitle = 'Welcome to EtiPOS Learning Center';
    const subtitle = 'Master your business tools in minutes, not hours';

    useEffect(() => {
        let index = 0;
        const timer = setInterval(() => {
            if (index <= fullTitle.length) {
                setTitleText(fullTitle.slice(0, index));
                index++;
            } else {
                clearInterval(timer);
            }
        }, 50);

        return () => clearInterval(timer);
    }, []);

    const stats = [
        { icon: BookOpen, label: 'Lessons', value: '30' },
        { icon: Clock, label: 'Hours of Learning', value: '4.5' },
        { icon: GraduationCap, label: 'Modules', value: '6' },
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="relative mb-16"
        >
            <div className="text-center">
                {/* Logo/Icon */}
                <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 15,
                        delay: 0.2
                    }}
                    className="inline-block mb-6"
                >
                    <div className="relative">
                        <motion.div
                            animate={{
                                boxShadow: [
                                    '0 0 20px rgba(59, 130, 246, 0.3)',
                                    '0 0 40px rgba(139, 92, 246, 0.3)',
                                    '0 0 20px rgba(59, 130, 246, 0.3)'
                                ]
                            }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center"
                        >
                            <GraduationCap className="w-10 h-10 text-white" />
                        </motion.div>
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                            className="absolute -top-2 -right-2"
                        >
                            <Sparkles className="w-6 h-6 text-yellow-400" />
                        </motion.div>
                    </div>
                </motion.div>

                {/* Title with Typewriter Effect */}
                <h1 className="text-5xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 dark:from-white dark:via-blue-200 dark:to-purple-200 bg-clip-text text-transparent">
                    {titleText}
                    <motion.span
                        animate={{ opacity: [1, 0] }}
                        transition={{ duration: 0.8, repeat: Infinity, repeatType: 'reverse' }}
                        className="inline-block ml-1"
                    >
                        |
                    </motion.span>
                </h1>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5 }}
                    className="text-xl text-slate-600 dark:text-slate-300 mb-8"
                >
                    {subtitle}
                </motion.p>

                {/* Search Bar */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="max-w-2xl mx-auto mb-8"
                >
                    <div className="relative group">
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="relative"
                        >
                            <motion.div
                                className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur-xl opacity-25 group-hover:opacity-50 transition-opacity duration-300"
                                animate={{
                                    scale: [1, 1.05, 1],
                                }}
                                transition={{ duration: 2, repeat: Infinity }}
                            />
                            <div
                                onClick={onSearchFocus}
                                className="relative bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-4 cursor-pointer hover:shadow-2xl transition-all duration-300"
                            >
                                <div className="flex items-center space-x-3">
                                    <Search className="w-6 h-6 text-slate-400" />
                                    <span className="flex-1 text-left text-slate-500 dark:text-slate-400">
                                        Search tutorials, features, or questions...
                                    </span>
                                    <kbd className="hidden sm:inline-flex items-center space-x-1 px-3 py-1 bg-slate-100 dark:bg-slate-700 rounded-lg text-sm text-slate-500 dark:text-slate-400 font-mono border border-slate-200 dark:border-slate-600">
                                        <Command className="w-3 h-3" />
                                        <span>K</span>
                                    </kbd>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="flex justify-center space-x-8 md:space-x-12 mb-8"
                >
                    {stats.map((stat, index) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8 + index * 0.1 }}
                            className="text-center"
                        >
                            <motion.div
                                whileHover={{ scale: 1.1 }}
                                className="inline-block"
                            >
                                <stat.icon className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                            </motion.div>
                            <div className="text-2xl font-bold text-slate-900 dark:text-white">
                                {stat.value}
                            </div>
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                                {stat.label}
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Resume Learning Card */}
                {resumeLearning && overallProgress > 0 && overallProgress < 100 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.2 }}
                        className="max-w-md mx-auto"
                    >
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => onResumeLearning(resumeLearning.lesson)}
                            className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl p-4 shadow-lg hover:shadow-2xl transition-all duration-300 relative overflow-hidden group"
                        >
                            <motion.div
                                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                                animate={{ x: ['-100%', '100%'] }}
                                transition={{ duration: 1.5, repeat: Infinity, delay: 2 }}
                            />
                            <div className="relative flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                                        <ArrowRight className="w-5 h-5" />
                                    </div>
                                    <div className="text-left">
                                        <div className="font-semibold">Continue Learning</div>
                                        <div className="text-sm text-white/80">Pick up where you left off</div>
                                    </div>
                                </div>
                                <div className="text-3xl font-bold">{Math.round(overallProgress)}%</div>
                            </div>
                        </motion.button>
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
};